import { Component, type ErrorInfo, type ReactNode } from 'react';

import { isDynamicImportError, logError, reloadOnce } from '../../utils/log';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

function errorText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return String(error);
}

const fallbackContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  padding: '24px',
  textAlign: 'center',
  gap: '12px',
};

// Deliberately dependency-light plain markup: this renders when the app tree has
// already crashed, so it must not rely on lazy chunks, i18n, or theme providers
// that may themselves be the thing that failed to load.
function DefaultFallback(): ReactNode {
  return (
    <div role="alert" style={fallbackContainerStyle}>
      <h1>Something went wrong</h1>
      <p>Please reload the page. If the problem persists, try again shortly.</p>
      <button onClick={() => window.location.reload()} type="button">
        Reload
      </button>
    </div>
  );
}

// Global render-error safety net. React 17 has no error boundary anywhere in this
// app, so any uncaught render error (React #321, RangeError, an import() that
// rejects and escapes Suspense) unmounts the whole tree to a blank page. This
// converts that into either a one-shot reload (transient deploy/chunk failures,
// which self-heal) or a recoverable fallback for genuine crashes.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    const text = errorText(error);
    // A rejected dynamic import / preload after a deploy is transient — reload once
    // (no-ops if we already refreshed) to pull the current bundle instead of logging.
    if (isDynamicImportError(text)) {
      reloadOnce();
      return;
    }
    // Genuine render crash: forward to OTEL (logError routes through the intercepted
    // console.error) so it is still observable while the user sees the fallback.
    logError(`ErrorBoundary caught: ${text}\n${info.componentStack ?? ''}`);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultFallback />;
    }
    return this.props.children;
  }
}
